import { usePathname } from "next/navigation";
import { BreadcrumbJsonLd } from "next-seo";
import { useEffect, useMemo, useRef } from "react";
import ArticleComponent from "@/components/article/ArticleComponent";
import { Breadcrumb } from "@/components/Breadcrumb";
import ErrorBoundary from "@/components/empty/ErrorBoundary";
import { DYNAMIC_ODDS_KEY } from "@/components/PortableText/PortableText";
import RelatedArticlesContainer from "@/components/related/RelatedArticlesContainer";
import RecentStories from "@/components/sidebar/RecentStories";
import Shared from "@/components/social/Shared";
import { ARTICLE_PAGE_VIEW } from "@/constants/amplitude";
import { Article, Promotion } from "@/graphql/gql/graphql";
import { BreadcrumbSegment } from "@/types/breadcrumbs";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import { useBreadcrumbs } from "@/utils/hooks/useBreadcrumbs";
import { useLinkCopiedNotification } from "@/utils/hooks/useLinkCopiedNotification";
import useRouterLoading from "@/utils/hooks/useRouterLoading";
import { hasKeyValuePair } from "@/utils/object";
import PromotionsWidget from "../sidebar/PromotionsWidget/PromotionsWidget";
import styles from "./articleContainer.module.scss";

type ArticleProps = {
  article: Article;
  promotions?: Promotion[];
};

const ArticleContainer = ({ article, promotions }: ArticleProps) => {
  const { showNotification, notify } = useLinkCopiedNotification();
  const pathname = usePathname();

  const parsedArticle = useMemo(
    () => ({
      ...article,
      parsedBody: JSON.parse(article.body),
    }),
    [article]
  );

  const {
    author: ArticleAuthor,
    title,
    sport: { name: sportName, slug: sportSlug },
    dataskriveId,
    publishedDate,
    firstPublishedAt,
  } = parsedArticle;

  const { setPage, logAmplitudeEvent } = useAmplitude();
  const loggedRef = useRef<string | null>(null);

  useEffect(() => {
    if (loggedRef.current !== title) {
      const analyticsOptions: Record<string, string> = {
        Competition: sportName,
        "Article Name": title,
        Author: ArticleAuthor.name,
        "First Published Date": firstPublishedAt ?? "",
        "Published Date": publishedDate ?? "",
        "Bet Enabled": hasKeyValuePair(parsedArticle.parsedBody, [
          "_type",
          DYNAMIC_ODDS_KEY,
        ]).toString(),
      };
      if (dataskriveId) {
        analyticsOptions["DataSkrive ID"] = dataskriveId;
      }
      logAmplitudeEvent(ARTICLE_PAGE_VIEW, analyticsOptions, {
        page: "article",
      });
      loggedRef.current = title;
      setPage("article");
    }
  }, [
    logAmplitudeEvent,
    sportName,
    title,
    article.seo.title,
    setPage,
    ArticleAuthor.name,
    dataskriveId,
    firstPublishedAt,
    publishedDate,
    parsedArticle.parsedBody,
  ]);

  const { loading } = useRouterLoading();

  const breadcrumbSegments: BreadcrumbSegment[] = [
    { name: sportName.toUpperCase(), path: `/${sportSlug}` },
    { name: title, path: pathname },
  ];

  const { trail, itemListElements } = useBreadcrumbs(breadcrumbSegments);

  return (
    <ErrorBoundary>
      <BreadcrumbJsonLd itemListElements={itemListElements} />
      <div className={styles.container}>
        <div className={styles.article}>
          <Breadcrumb trail={trail} />

          <ArticleComponent
            article={parsedArticle}
            notify={notify}
            loading={loading}
          />
        </div>
        <aside className={styles.recentStories}>
          <RecentStories />
          <PromotionsWidget
            promotions={promotions}
            articleName={title}
            sport={sportName}
          />
        </aside>
        <div className={styles.moreArticles}>
          <RelatedArticlesContainer sportName={sportName} />
        </div>
        <div className={styles.sharedContainer}>
          <div className={styles.sharedSubContainer}>
            {showNotification && <Shared />}
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default ArticleContainer;
